/**
 * Segmentanalyse — ABC-Segmentierung und Controlling-Analyse mit Kostenstellen.
 *
 * Features:
 * - Segment-Verwaltung (A/B/C erstellen, Farben, Reihenfolge)
 * - Kostenstellen-Mapping zu Segmenten
 * - Controlling-Analyse: Umsatz, Kosten, DB pro Segment mit Trend
 * - Zeitraum-Filter (Monat/Quartal/Jahr/Custom), Vergleichsperiode
 * - Pie-, Bar- und Line-Charts (Recharts)
 *
 * @page /kostenstellen
 * @roles admin, manager
 * @api /api/segmente, /api/segmente/mapping, /api/controlling/analyse
 * @api /api/odoo/kostenstellen
 * @i18n segments
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line, Legend,
} from 'recharts';
import { NavLink } from 'react-router-dom';
import useTabParam from '../hooks/useTabParam';
import { PageHeader, ToggleSwitch } from '../components/ui';
import { lastDayOfMonth } from '../utils/dateUtils';
import { useLanguage } from '../context/LanguageContext';

/* ─── API helper ─── */
function api(path, opts = {}) {
  const hasBody = opts.body != null;
  return fetch(path, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.message || `HTTP ${r.status}`);
    return data;
  });
}

/* ─── Formatters ─── */
function fmt(n) {
  if (n == null || n === 0) return '0,00 €';
  return Math.abs(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function fmtSigned(n) {
  if (n == null || n === 0) return '0,00 €';
  const s = Math.abs(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  return n < 0 ? `−${s}` : s;
}

function fmtPct(n) {
  if (n == null) return '0,0 %';
  return n.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %';
}

function fmtCompact(n) {
  if (Math.abs(n) >= 1000)
    return (n / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 }) + 'K';
  return n.toLocaleString('de-DE', { maximumFractionDigits: 0 });
}

/* MONTHS removed — use t('common.months.X') via useLanguage */
const PRESET_COLORS = ['#338edc','#22D3EE','#6366F1','#E5E7EB','#4D9CE0','#66ABE5','#06b6d4','#818CF8','#67E8F9','#8FC2EE','#B8D8F5','#9CA3AF'];

/* ─── Shared style tokens ─── */
const TH = 'py-3 px-4';
const TD = 'py-2 px-4';
const TOOLTIP_STYLE = {
  contentStyle: { background: 'var(--bg-elevated, #1f2937)', border: '1px solid var(--border-hover, rgba(255,255,255,0.1))', borderRadius: 8, fontSize: 12, color: 'var(--text-primary, #fff)' },
  itemStyle: { color: 'var(--text-secondary, #d1d5db)' },
  labelStyle: { color: 'var(--text-muted, #9ca3af)', fontWeight: 600 },
};

/* ─── Helpers ─── */
function margeColor(pct) {
  return pct > 20 ? 'text-green-400' : pct >= 0 ? 'text-yellow-400' : 'text-red-400';
}

function margeBadge(pct) {
  return pct > 20
    ? 'bg-green-500/15 text-green-400 border-green-500/20'
    : pct >= 0
      ? 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20'
      : 'bg-red-500/15 text-red-400 border-red-500/20';
}

/* ─── Chevron Icon ─── */
function ChevronIcon({ open }) {
  return (
    <svg
      width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
      className={`transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
    >
      <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ─── Delta Badge ─── */
function DeltaBadge({ value, pct }) {
  if (value == null || value === 0)
    return <span className="text-[var(--text-tertiary)] text-xs">–</span>;
  const positive = value > 0;
  const arrow = positive ? '↑' : '↓';
  const color = positive ? 'text-green-400' : 'text-red-400';
  return (
    <span className={`text-xs font-mono ${color}`}>
      {arrow} {fmtSigned(value)}{' '}
      {pct != null && <span className="text-[10px] opacity-70">({fmtPct(pct)})</span>}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/* FilterBar                                                  */
/* ═══════════════════════════════════════════════════════════ */
function FilterBar({ filter, onChange }) {
  const { t } = useLanguage();
  const modes = [
    { value: 'monat', label: t('segments.filter_month') },
    { value: 'quartal', label: t('segments.filter_quarter') },
    { value: 'jahr', label: t('segments.filter_year') },
    { value: 'custom', label: t('segments.filter_custom') },
  ];
  const set = (patch) => onChange({ ...filter, ...patch });

  return (
    <div className="flex flex-wrap items-center gap-3 mb-6">
      {/* Mode selector */}
      <div className="flex bg-surface-inset rounded-lg p-0.5">
        {modes.map((m) => (
          <button
            key={m.value}
            onClick={() => set({ mode: m.value })}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filter.mode === m.value ? 'bg-surface-card text-content font-semibold shadow-sm' : 'text-[var(--text-secondary)] font-normal hover:text-content-secondary'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Monat selectors */}
      {filter.mode === 'monat' && (
        <>
          <select
            value={filter.monat}
            onChange={(e) => set({ monat: +e.target.value })}
            className="px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content focus:outline-none focus:border-primary-500/50 appearance-none cursor-pointer"
          >
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i} value={i + 1} className="bg-surface-elevated">{t(`common.months.${i}`)}</option>
            ))}
          </select>
          <select
            value={filter.jahr}
            onChange={(e) => set({ jahr: +e.target.value })}
            className="px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content focus:outline-none focus:border-primary-500/50 appearance-none cursor-pointer"
          >
            {[2024, 2025, 2026].map((y) => (
              <option key={y} value={y} className="bg-surface-elevated">{y}</option>
            ))}
          </select>
        </>
      )}

      {/* Quartal selectors */}
      {filter.mode === 'quartal' && (
        <>
          <div className="flex bg-surface-inset rounded-lg p-0.5">
            {[1, 2, 3, 4].map((q) => (
              <button
                key={q}
                onClick={() => set({ quartal: q })}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  filter.quartal === q ? 'bg-surface-card text-content font-semibold shadow-sm' : 'text-[var(--text-secondary)] font-normal hover:text-content-secondary'
                }`}
              >
                Q{q}
              </button>
            ))}
          </div>
          <select
            value={filter.jahr}
            onChange={(e) => set({ jahr: +e.target.value })}
            className="px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content focus:outline-none focus:border-primary-500/50 appearance-none cursor-pointer"
          >
            {[2024, 2025, 2026].map((y) => (
              <option key={y} value={y} className="bg-surface-elevated">{y}</option>
            ))}
          </select>
        </>
      )}

      {/* Jahr selector */}
      {filter.mode === 'jahr' && (
        <select
          value={filter.jahr}
          onChange={(e) => set({ jahr: +e.target.value })}
          className="px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content focus:outline-none focus:border-primary-500/50 appearance-none cursor-pointer"
        >
          {[2024, 2025, 2026].map((y) => (
            <option key={y} value={y} className="bg-surface-elevated">{y}</option>
          ))}
        </select>
      )}

      {/* Custom date range */}
      {filter.mode === 'custom' && (
        <>
          <input
            type="date"
            value={filter.customVon}
            onChange={(e) => set({ customVon: e.target.value })}
            className="px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content focus:outline-none focus:border-primary-500/50"
          />
          <span className="text-[var(--text-tertiary)]">–</span>
          <input
            type="date"
            value={filter.customBis}
            onChange={(e) => set({ customBis: e.target.value })}
            className="px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content focus:outline-none focus:border-primary-500/50"
          />
        </>
      )}

      {/* Vergleich toggle */}
      <div className="ml-auto flex items-center gap-2">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <ToggleSwitch size="sm" checked={filter.vergleich} onChange={(v) => set({ vergleich: v })} />
          <span className="text-xs text-content-tertiary">{t('segments.vs_previous_period')}</span>
        </label>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/* SegmentCard                                                */
/* ═══════════════════════════════════════════════════════════ */
function SegmentCard({ seg, showVergleich }) {
  const { t } = useLanguage();
  return (
    <div className="bg-surface-card border border-line rounded-card p-4 hover:bg-surface-hover transition-colors">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: seg.farbe }} />
        <span className="text-sm font-semibold text-content truncate flex-1">{seg.name}</span>
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-[var(--text-primary)]">
          <span className={`w-2 h-2 rounded-full shrink-0 ${seg.marge_pct > 20 ? 'bg-green-400' : seg.marge_pct >= 0 ? 'bg-yellow-400' : 'bg-red-400'}`} />
          {fmtPct(seg.marge_pct)}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        <div>
          <div className="text-[10px] text-[var(--text-secondary)] uppercase">{t('segments.revenue')}</div>
          <div className="text-sm font-mono text-content">{fmt(seg.umsatz)}</div>
        </div>
        <div>
          <div className="text-[10px] text-[var(--text-secondary)] uppercase">{t('segments.costs')}</div>
          <div className="text-sm font-mono text-content">{fmtSigned(seg.kosten)}</div>
        </div>
        <div>
          <div className="text-[10px] text-[var(--text-secondary)] uppercase">{t('segments.balance')}</div>
          <div className="text-sm font-mono font-bold text-content">
            {fmtSigned(seg.saldo)}
          </div>
          {showVergleich && seg.vergleich && (
            <DeltaBadge value={seg.vergleich.delta_saldo} pct={seg.vergleich.delta_saldo_pct} />
          )}
        </div>
        <div>
          <div className="text-[10px] text-[var(--text-secondary)] uppercase">{t('segments.margin')}</div>
          <div className="text-sm font-mono font-bold text-content">
            {fmtPct(seg.marge_pct)}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/* SaldoBarChart                                              */
/* ═══════════════════════════════════════════════════════════ */
function SaldoBarChart({ segmente }) {
  const { t } = useLanguage();
  const data = [...segmente].filter((s) => s.saldo !== 0).sort((a, b) => b.saldo - a.saldo);
  if (!data.length) return null;
  return (
    <div className="bg-surface-card border border-line rounded-card p-4">
      <div className="text-xs font-semibold text-content-tertiary mb-3">{t('segments.balance_per_segment')}</div>
      <ResponsiveContainer width="100%" height={Math.max(200, data.length * 40)}>
        <BarChart layout="vertical" data={data} margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary, rgba(255,255,255,0.05))" horizontal={false} />
          <XAxis type="number" tickFormatter={(v) => fmtCompact(v)} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} />
          <YAxis type="category" dataKey="name" width={120} tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} axisLine={false} />
          <Tooltip formatter={(v) => fmtSigned(v)} {...TOOLTIP_STYLE} />
          <Bar dataKey="saldo" radius={[0, 4, 4, 0]}>
            {data.map((s, i) => (
              <Cell key={i} fill={s.farbe} fillOpacity={0.8} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/* KostenPieChart                                             */
/* ═══════════════════════════════════════════════════════════ */
function KostenPieChart({ segmente }) {
  const { t } = useLanguage();
  const data = segmente
    .filter((s) => s.kosten !== 0)
    .map((s) => ({ name: s.name, value: Math.abs(s.kosten), farbe: s.farbe }));
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!data.length) return null;
  return (
    <div className="bg-surface-card border border-line rounded-card p-4">
      <div className="text-xs font-semibold text-content-tertiary mb-3">{t('segments.cost_distribution')}</div>
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={95}
            paddingAngle={2}
            strokeWidth={0}
          >
            {data.map((d, i) => (
              <Cell key={i} fill={d.farbe} fillOpacity={0.85} />
            ))}
          </Pie>
          <Tooltip formatter={(v) => `${fmt(v)} (${fmtPct(total > 0 ? (v / total) * 100 : 0)})`} {...TOOLTIP_STYLE} />
          <Legend wrapperStyle={{ fontSize: 11, color: 'var(--text-muted)' }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/* VergleichBarChart                                          */
/* ═══════════════════════════════════════════════════════════ */
function VergleichBarChart({ segmente }) {
  const { t } = useLanguage();
  const data = segmente
    .filter((s) => s.vergleich)
    .map((s) => ({ name: s.name, aktuell: s.saldo, vorperiode: s.vergleich.saldo, farbe: s.farbe }));
  if (!data.length) return null;
  return (
    <div className="bg-surface-card border border-line rounded-card p-4">
      <div className="text-xs font-semibold text-content-tertiary mb-3">{t('segments.current_vs_previous')}</div>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary, rgba(255,255,255,0.05))" />
          <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} interval={0} angle={-20} textAnchor="end" height={50} />
          <YAxis tickFormatter={(v) => fmtCompact(v)} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} />
          <Tooltip formatter={(v) => fmtSigned(v)} {...TOOLTIP_STYLE} />
          <Bar dataKey="aktuell" name={t('segments.current')} fill="#22d3ee" fillOpacity={0.8} radius={[4, 4, 0, 0]} />
          <Bar dataKey="vorperiode" name={t('segments.previous_period')} fill="#6b7280" fillOpacity={0.5} radius={[4, 4, 0, 0]} />
          <Legend wrapperStyle={{ fontSize: 11, color: 'var(--text-muted)' }} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/* TrendLineChart                                             */
/* ═══════════════════════════════════════════════════════════ */
function TrendLineChart({ trend, segmente }) {
  const { t } = useLanguage();
  if (!trend || !trend.length) return null;
  const activeSegs = segmente.filter((s) => s.saldo !== 0).map((s) => ({ name: s.name, farbe: s.farbe }));
  const chartData = trend.map((tr) => {
    const row = { monat: tr.monat };
    for (const seg of activeSegs) row[seg.name] = tr.segmente[seg.name]?.saldo || 0;
    return row;
  });
  return (
    <div className="bg-surface-card border border-line rounded-card p-4">
      <div className="text-xs font-semibold text-content-tertiary mb-3">{t('segments.balance_trend_6m')}</div>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary, rgba(255,255,255,0.05))" />
          <XAxis dataKey="monat" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} />
          <YAxis tickFormatter={(v) => fmtCompact(v)} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} />
          <Tooltip formatter={(v) => fmtSigned(v)} {...TOOLTIP_STYLE} />
          {activeSegs.map((seg) => (
            <Line key={seg.name} type="monotone" dataKey={seg.name} stroke={seg.farbe} strokeWidth={2} dot={false} />
          ))}
          <Legend wrapperStyle={{ fontSize: 11, color: 'var(--text-muted)' }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/* DetailTable                                                */
/* ═══════════════════════════════════════════════════════════ */
function DetailTable({ data, showVergleich, showEmpty }) {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState({});
  const [sortKey, setSortKey] = useState('saldo');
  const [sortDir, setSortDir] = useState('desc');

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };
  const arrow = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');
  const sorted = (arr) => [...arr].sort((a, b) => {
    const va = a[sortKey] ?? 0;
    const vb = b[sortKey] ?? 0;
    return sortDir === 'asc' ? va - vb : vb - va;
  });

  const visible = showEmpty ? data.segmente : data.segmente.filter((s) => s.umsatz !== 0 || s.kosten !== 0);
  const colSpan = showVergleich ? 8 : 7;

  return (
    <div className="bg-surface-card border border-line rounded-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 40 }} />
            <col />
            <col style={{ width: 100 }} />
            <col style={{ width: 140 }} />
            <col style={{ width: 140 }} />
            <col style={{ width: 140 }} />
            <col style={{ width: 100 }} />
            {showVergleich && <col style={{ width: 140 }} />}
          </colgroup>
          <thead>
            <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
              <th className={TH} />
              <th className={`${TH} text-left`}>{t('segments.segment_cost_center')}</th>
              <th className={`${TH} text-left`}>{t('segments.code')}</th>
              <th className={`${TH} text-right cursor-pointer hover:text-content-secondary select-none`} onClick={() => handleSort('umsatz')}>
                {t('segments.revenue')}{arrow('umsatz')}
              </th>
              <th className={`${TH} text-right cursor-pointer hover:text-content-secondary select-none`} onClick={() => handleSort('kosten')}>
                {t('segments.costs')}{arrow('kosten')}
              </th>
              <th className={`${TH} text-right cursor-pointer hover:text-content-secondary select-none`} onClick={() => handleSort('saldo')}>
                {t('segments.balance')}{arrow('saldo')}
              </th>
              <th className={`${TH} text-right cursor-pointer hover:text-content-secondary select-none`} onClick={() => handleSort('marge_pct')}>
                {t('segments.margin_pct')}{arrow('marge_pct')}
              </th>
              {showVergleich && <th className={`${TH} text-right`}>{'Δ'} {t('segments.balance')}</th>}
            </tr>
          </thead>

          {/* Segment rows */}
          {visible.map((seg) => {
            const isOpen = expanded[seg.id];
            const hasKS = seg.kostenstellen.length > 0;
            return (
              <tbody key={seg.id}>
                <tr
                  onClick={() => hasKS && toggle(seg.id)}
                  className={`border-b border-line transition-colors ${hasKS ? 'cursor-pointer hover:bg-surface-card' : ''} ${isOpen ? 'bg-surface-card' : ''}`}
                >
                  <td className={TH + ' text-[var(--text-tertiary)]'}>{hasKS && <ChevronIcon open={isOpen} />}</td>
                  <td className={TH}>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: seg.farbe }} />
                      <span className="text-content font-semibold">{seg.name}</span>
                      <span className="text-[10px] text-[var(--text-tertiary)] ml-1">({seg.kostenstellen.length})</span>
                    </div>
                  </td>
                  <td className={TH} />
                  <td className={`${TH} text-right font-mono text-content`}>{fmt(seg.umsatz)}</td>
                  <td className={`${TH} text-right font-mono text-content`}>{fmtSigned(seg.kosten)}</td>
                  <td className={`${TH} text-right font-mono font-bold text-content`}>
                    {fmtSigned(seg.saldo)}
                  </td>
                  <td className={`${TH} text-right font-mono text-content`}>{fmtPct(seg.marge_pct)}</td>
                  {showVergleich && (
                    <td className={`${TH} text-right`}>
                      <DeltaBadge value={seg.vergleich?.delta_saldo} pct={seg.vergleich?.delta_saldo_pct} />
                    </td>
                  )}
                </tr>

                {/* Expanded Kostenstellen */}
                {isOpen && sorted(seg.kostenstellen).map((ks) => (
                  <tr key={ks.code} className="bg-surface-inset border-b border-line">
                    <td className={TD} />
                    <td className={`${TD} text-xs text-content-tertiary pl-10`}>{ks.name}</td>
                    <td className={`${TD} text-xs font-mono text-[var(--text-secondary)]`}>{ks.code}</td>
                    <td className={`${TD} text-right text-xs font-mono text-content-secondary`}>{fmt(ks.umsatz)}</td>
                    <td className={`${TD} text-right text-xs font-mono text-content-secondary`}>{fmtSigned(ks.kosten)}</td>
                    <td className={`${TD} text-right text-xs font-mono text-content-secondary`}>
                      {fmtSigned(ks.saldo)}
                    </td>
                    <td className={`${TD} text-right text-xs font-mono text-content-secondary`}>{fmtPct(ks.marge_pct)}</td>
                    {showVergleich && <td className={TD} />}
                  </tr>
                ))}
              </tbody>
            );
          })}

          {/* Nicht zugeordnet */}
          {data.nicht_zugeordnet?.length > 0 && (
            <tbody>
              <tr className="border-b border-line bg-amber-500/[0.03]">
                <td className={TH} />
                <td className={TH} colSpan={colSpan - 1}>
                  <span className="inline-flex items-center gap-2 text-amber-400 font-semibold text-sm">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    {t('segments.unassigned')} ({data.nicht_zugeordnet.length})
                  </span>
                </td>
              </tr>
              {data.nicht_zugeordnet.map((ks) => (
                <tr key={ks.code} className="bg-surface-inset border-b border-line">
                  <td className={TD} />
                  <td className={`${TD} text-xs text-content-tertiary pl-10`}>{ks.name}</td>
                  <td className={`${TD} text-xs font-mono text-[var(--text-secondary)]`}>{ks.code}</td>
                  <td className={`${TD} text-right text-xs font-mono text-content-secondary`}>{fmt(ks.umsatz)}</td>
                  <td className={`${TD} text-right text-xs font-mono text-content-secondary`}>{fmtSigned(ks.kosten)}</td>
                  <td className={`${TD} text-right text-xs font-mono text-content-secondary`}>
                    {fmtSigned(ks.saldo)}
                  </td>
                  <td className={`${TD} text-right text-xs font-mono text-content-secondary`}>{fmtPct(ks.marge_pct)}</td>
                  {showVergleich && <td className={TD} />}
                </tr>
              ))}
            </tbody>
          )}

          {/* Footer: Gesamt */}
          <tfoot>
            <tr className="border-t-2 border-line-hover bg-surface-card">
              <td className={TH} />
              <td className={`${TH} text-content font-bold`} colSpan={2}>{t('segments.total_label')}</td>
              <td className={`${TH} text-right font-mono font-bold text-content`}>{fmt(data.gesamt.umsatz)}</td>
              <td className={`${TH} text-right font-mono font-bold text-content`}>{fmtSigned(data.gesamt.kosten)}</td>
              <td className={`${TH} text-right font-mono font-extrabold text-content`}>
                {fmtSigned(data.gesamt.saldo)}
              </td>
              <td className={`${TH} text-right font-mono font-bold text-content`}>
                {fmtPct(data.gesamt.marge_pct)}
              </td>
              {showVergleich && (
                <td className={`${TH} text-right`}>
                  <DeltaBadge
                    value={data.gesamt.vergleich?.delta_saldo}
                    pct={data.gesamt.vergleich?.delta_saldo_pct}
                  />
                </td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/* ConfigPanel (side drawer)                                  */
/* ═══════════════════════════════════════════════════════════ */
function ConfigPanel({ open, onClose, onRefresh, odooKostenstellen }) {
  const { t } = useLanguage();
  const [tab, setTab] = useTabParam('segmente');
  const [segmente, setSegmente] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#10b981');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api('/api/segmente').then(setSegmente).catch(() => {});
      api('/api/segmente/mapping').then(setMappings).catch(() => {});
    }
  }, [open]);

  const addSegment = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const seg = await api('/api/segmente', { method: 'POST', body: { name: newName.trim(), farbe: newColor, reihenfolge: segmente.length + 1 } });
      setSegmente((prev) => [...prev, seg]);
      setNewName('');
    } catch {}
    setSaving(false);
  };

  const deleteSegment = async (id) => {
    await api(`/api/segmente/${id}`, { method: 'DELETE' });
    setSegmente((prev) => prev.filter((s) => s.id !== id));
  };

  const assignMapping = async (code, name, segmentId) => {
    if (!segmentId) {
      await api(`/api/segmente/mapping/${encodeURIComponent(code)}`, { method: 'DELETE' });
      setMappings((prev) => prev.filter((m) => m.kostenstelle_code !== code));
    } else {
      const m = await api('/api/segmente/mapping', { method: 'POST', body: { kostenstelle_code: code, kostenstelle_name: name, segment_id: segmentId } });
      setMappings((prev) => {
        const idx = prev.findIndex((x) => x.kostenstelle_code === code);
        if (idx >= 0) { const copy = [...prev]; copy[idx] = m; return copy; }
        return [...prev, m];
      });
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-[480px] max-w-[90vw] bg-surface border-l border-line-hover z-50 flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <h2 className="text-lg font-bold text-content">{t('segments.config_title')}</h2>
          <button
            onClick={() => { onClose(); onRefresh(); }}
            className="text-[var(--text-tertiary)] hover:text-content transition-colors"
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-line">
          {['segmente', 'zuordnung'].map((tabKey) => (
            <button
              key={tabKey}
              onClick={() => setTab(tabKey)}
              className={`flex-1 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                tab === tabKey ? 'text-content border-b-2 border-primary-500' : 'text-[var(--text-secondary)] hover:text-content-secondary'
              }`}
            >
              {tabKey === 'segmente' ? t('segments.tab_segments') : t('segments.tab_assignment')}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'segmente' && (
            <div className="space-y-2">
              {segmente.map((seg) => (
                <div key={seg.id} className="flex items-center gap-3 bg-surface-card rounded-card px-3 py-2.5">
                  <span className="w-4 h-4 rounded-full flex-shrink-0 border border-line-hover" style={{ backgroundColor: seg.farbe }} />
                  <span className="text-sm text-content flex-1">{seg.name}</span>
                  <span className="text-[10px] text-[var(--text-tertiary)] font-mono">#{seg.reihenfolge}</span>
                  <button onClick={() => deleteSegment(seg.id)} className="text-[var(--text-tertiary)] hover:text-red-400 transition-colors">
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}

              {/* Color picker */}
              <div className="flex items-center gap-2 pt-3 border-t border-line">
                <div className="flex gap-1 flex-wrap">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setNewColor(c)}
                      className={`w-5 h-5 rounded-full border-2 transition-colors ${newColor === c ? 'border-white' : 'border-transparent'}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>

              {/* Add segment */}
              <div className="flex gap-2">
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={t('segments.new_segment_placeholder')}
                  className="flex-1 px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-primary-500/50"
                  onKeyDown={(e) => e.key === 'Enter' && addSegment()}
                />
                <button
                  onClick={addSegment}
                  disabled={saving || !newName.trim()}
                  className="px-4 py-2 bg-[#338edc]/20 text-primary-400 rounded-lg text-sm font-medium hover:bg-[#338edc]/30 transition-colors disabled:opacity-40"
                >
                  +
                </button>
              </div>
            </div>
          )}

          {tab === 'zuordnung' && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 mb-3">
                                <div className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider">
                  {t('segments.cost_center_to_segment')}
                </div>
              </div>
              {odooKostenstellen.map((ks) => {
                const current = mappings.find((m) => m.kostenstelle_code === (ks.code || String(ks.id)));
                return (
                  <div key={ks.id} className="flex items-center gap-3 bg-surface-card rounded-card px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-content truncate">{ks.name}</div>
                      <div className="text-[10px] text-[var(--text-tertiary)] font-mono">{ks.code || ks.id}</div>
                    </div>
                    <select
                      value={current?.segment_id || ''}
                      onChange={(e) => assignMapping(ks.code || String(ks.id), ks.name, e.target.value ? Number(e.target.value) : null)}
                      className="px-2 py-1.5 bg-surface-inset border border-line-hover rounded-lg text-xs text-content focus:outline-none focus:border-primary-500/50 appearance-none cursor-pointer min-w-[140px]"
                    >
                      <option value="" className="bg-surface-elevated">– {t('segments.unassigned')}</option>
                      {segmente.map((s) => (
                        <option key={s.id} value={s.id} className="bg-surface-elevated">{s.name}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
              {odooKostenstellen.length === 0 && (
                <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('segments.no_cost_centers_loaded')}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════ */
/* MAIN: Segmentanalyse                                       */
/* ═══════════════════════════════════════════════════════════ */
export default function Segmentanalyse() {
  const { t } = useLanguage();
  const now = new Date();
  const [filter, setFilter] = useState({
    mode: 'monat',
    monat: now.getMonth() + 1,
    jahr: now.getFullYear(),
    quartal: Math.ceil((now.getMonth() + 1) / 3),
    customVon: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`,
    customBis: lastDayOfMonth(now.getFullYear(), now.getMonth() + 1),
    vergleich: false,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [odooKS, setOdooKS] = useState([]);
  const [showEmpty, setShowEmpty] = useState(false);
  const [search, setSearch] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('');

  /* Compute date range from filter */
  const dateRange = useMemo(() => {
    const { mode, monat, jahr, quartal, customVon, customBis } = filter;
    if (mode === 'monat') {
      const von = `${jahr}-${String(monat).padStart(2, '0')}-01`;
      const bis = lastDayOfMonth(jahr, monat);
      return { von, bis };
    }
    if (mode === 'quartal') {
      const startMonth = (quartal - 1) * 3 + 1;
      const von = `${jahr}-${String(startMonth).padStart(2, '0')}-01`;
      const bis = lastDayOfMonth(jahr, startMonth + 2);
      return { von, bis };
    }
    if (mode === 'jahr') return { von: `${jahr}-01-01`, bis: `${jahr}-12-31` };
    return { von: customVon, bis: customBis };
  }, [filter]);

  /* Build vergleich query param */
  const vergleichParam = useMemo(() => {
    if (!filter.vergleich) return '';
    if (filter.mode === 'monat') return '&vergleich=vormonat';
    if (filter.mode === 'quartal') return '&vergleich=vorquartal';
    if (filter.mode === 'jahr') return '&vergleich=vorjahr';
    return '&vergleich=vormonat';
  }, [filter.vergleich, filter.mode]);

  /* Load data */
  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);
    api(`/api/controlling/analyse?von=${dateRange.von}&bis=${dateRange.bis}${vergleichParam}&trend=1`)
      .then((d) => {
        if (d.error) { setError(d.message); setData(null); }
        else setData(d);
      })
      .catch((e) => { setError(e.message); setData(null); })
      .finally(() => setLoading(false));
  }, [dateRange, vergleichParam]);

  useEffect(() => { loadData(); }, [loadData]);

  /* Load Odoo Kostenstellen for config panel */
  useEffect(() => {
    api(`/api/odoo/kostenstellen?monat=${filter.monat}&jahr=${filter.jahr}`)
      .then((d) => { if (d.kostenstellen) setOdooKS(d.kostenstellen); })
      .catch(() => {});
  }, []);

  /* Period label */
  const periodLabel = useMemo(() => {
    if (filter.mode === 'monat') return `${t(`common.months.${filter.monat - 1}`)} ${filter.jahr}`;
    if (filter.mode === 'quartal') return `Q${filter.quartal} ${filter.jahr}`;
    if (filter.mode === 'jahr') return `${filter.jahr}`;
    return `${dateRange.von} – ${dateRange.bis}`;
  }, [filter, dateRange, t]);

  /* Filtered segments */
  const filteredSegmente = useMemo(() => {
    if (!data) return [];
    let segs = showEmpty ? data.segmente : data.segmente.filter((s) => s.umsatz !== 0 || s.kosten !== 0);
    if (segmentFilter) segs = segs.filter((s) => String(s.id) === segmentFilter);
    if (search) {
      const q = search.toLowerCase();
      segs = segs
        .map((s) => ({
          ...s,
          kostenstellen: s.kostenstellen.filter((ks) =>
            ks.name?.toLowerCase().includes(q) || ks.code?.toLowerCase().includes(q)
          ),
        }))
        .filter((s) => s.kostenstellen.length > 0 || s.name.toLowerCase().includes(q));
    }
    return segs;
  }, [data, showEmpty, segmentFilter, search]);

  return (
    <div className="p-page">
      <PageHeader title={t('segments.title')} subtitle={`${t('segments.subtitle')} · ${periodLabel}`} className="mb-6" actions={
        <div className="flex items-center gap-2">
          {data?.cache_hit && (
            <span className="text-[10px] text-[var(--text-tertiary)] font-mono">
              Cache ({data.cache_alter_sekunden}s)
            </span>
          )}
          {/* Show empty toggle */}
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <ToggleSwitch size="sm" checked={showEmpty} onChange={setShowEmpty} />
            <span className="text-[10px] text-[var(--text-secondary)]">{t('segments.show_empty')}</span>
          </label>
          {/* Refresh */}
          <button
            onClick={loadData}
            disabled={loading}
            className="px-3 py-2 bg-surface-inset border border-line-hover rounded-button text-sm text-content-tertiary hover:text-content hover:bg-surface-active transition-colors disabled:opacity-40"
          >
            {loading ? t('common.loading') : t('segments.refresh')}
          </button>
          {/* Config button */}
          <button
            onClick={() => setConfigOpen(true)}
            className="px-3 py-2 bg-surface-inset border border-line-hover rounded-button text-content-tertiary hover:text-content hover:bg-surface-active transition-colors"
            title={t('segments.config_title')}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
            </svg>
          </button>
        </div>
      } />

      {/* Filter bar */}
      <FilterBar filter={filter} onChange={setFilter} />

      {/* Search & segment filter */}
      {!loading && !error && data && (
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('segments.search_cost_center')}
              className="pl-9 pr-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none focus:border-primary-500/50 w-56"
            />
          </div>
          <select
            value={segmentFilter}
            onChange={(e) => setSegmentFilter(e.target.value)}
            className="px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content focus:outline-none focus:border-primary-500/50 appearance-none cursor-pointer"
          >
            <option value="" className="bg-surface-elevated">{t('segments.all_segments')}</option>
            {data.segmente.map((s) => (
              <option key={s.id} value={s.id} className="bg-surface-elevated">{s.name}</option>
            ))}
          </select>
          {(search || segmentFilter) && (
            <span className="text-xs text-[var(--text-secondary)]">
              {t('segments.filter_result', { segments: filteredSegmente.length, costCenters: filteredSegmente.reduce((s, seg) => s + seg.kostenstellen.length, 0) })}
            </span>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-sm text-[var(--text-tertiary)] py-12 text-center">{t('segments.loading_analysis')}</div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="bg-red-500/5 border border-red-500/10 rounded-card p-6 text-center">
          <div className="text-red-400 text-sm font-semibold mb-2">{t('segments.connection_error')}</div>
          <div className="text-xs text-[var(--text-secondary)] mb-4">{error}</div>
          <NavLink
            to="/api-config"
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600/20 border border-amber-500/30 rounded-lg text-sm font-medium text-amber-300 hover:bg-amber-600/30 transition-colors"
          >
            {t('segments.open_api_config')}
          </NavLink>
        </div>
      )}

      {/* Main content */}
      {!loading && !error && data && (
        <>
          {/* Segment Cards + Gesamt */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
            {filteredSegmente.map((seg) => (
              <SegmentCard key={seg.id} seg={seg} showVergleich={filter.vergleich} />
            ))}
            {/* Gesamt card */}
            <div className="bg-surface-inset border border-line-hover rounded-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-3 h-3 rounded-full bg-white/30 flex-shrink-0" />
                <span className="text-sm font-bold text-content flex-1">{t('common.total')}</span>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-[var(--text-primary)]">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${data.gesamt.marge_pct > 20 ? 'bg-green-400' : data.gesamt.marge_pct >= 0 ? 'bg-yellow-400' : 'bg-red-400'}`} />
                  {fmtPct(data.gesamt.marge_pct)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <div>
                  <div className="text-[10px] text-[var(--text-secondary)] uppercase">{t('segments.revenue')}</div>
                  <div className="text-sm font-mono text-content font-bold">{fmt(data.gesamt.umsatz)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-[var(--text-secondary)] uppercase">{t('segments.costs')}</div>
                  <div className="text-sm font-mono text-content font-bold">{fmtSigned(data.gesamt.kosten)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-[var(--text-secondary)] uppercase">{t('segments.balance')}</div>
                  <div className="text-sm font-mono font-extrabold text-content">
                    {fmtSigned(data.gesamt.saldo)}
                  </div>
                  {filter.vergleich && data.gesamt.vergleich && (
                    <DeltaBadge value={data.gesamt.vergleich.delta_saldo} pct={data.gesamt.vergleich.delta_saldo_pct} />
                  )}
                </div>
                <div>
                  <div className="text-[10px] text-[var(--text-secondary)] uppercase">{t('segments.margin')}</div>
                  <div className="text-sm font-mono font-extrabold text-content">
                    {fmtPct(data.gesamt.marge_pct)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Charts grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
            <SaldoBarChart segmente={filteredSegmente} />
            <KostenPieChart segmente={filteredSegmente} />
            {filter.vergleich
              ? <VergleichBarChart segmente={filteredSegmente} />
              : <TrendLineChart trend={data.trend} segmente={filteredSegmente} />
            }
            {filter.vergleich && <TrendLineChart trend={data.trend} segmente={filteredSegmente} />}
          </div>

          {/* Detail table */}
          <DetailTable data={data} showVergleich={filter.vergleich} showEmpty={showEmpty} />

          {/* Footer */}
          <div className="mt-3 flex items-center justify-between text-[10px] text-[var(--text-tertiary)]">
            <span>
              {t('segments.footer_summary', { count: filteredSegmente.length })} &middot; {dateRange.von} – {dateRange.bis}
            </span>
            <span className="font-mono">READ-ONLY &middot; Odoo XMLRPC</span>
          </div>
        </>
      )}

      {/* Config panel */}
      <ConfigPanel
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        onRefresh={loadData}
        odooKostenstellen={odooKS}
      />
    </div>
  );
}
